import type { WidgetType } from '@/types/dashboard';
import type { EntityListData, EntityListItemDto, EntityListPanelDto } from '@/types/entity-list';
import { WidgetRenderer } from '@/components/WidgetRenderer';
import type { Panel } from '@/types/dashboard';
import './EntityListWidget.css';

/**
 * Transforms raw backend widget data into the format expected by widget components.
 * Matches the transformation logic in Dashboard.tsx handlePanelVisible.
 */
function transformPanelData(panel: EntityListPanelDto): Record<string, unknown> {
  if (panel.type === 'indicator') {
    const main = (panel.data as any)?.main ?? {};
    return {
      value: main.value ?? null,
      delta: main.delta ?? null,
    };
  }
  if (panel.type === 'labeled_list') {
    return { values: panel.data };
  }
  // chart, entity_list, chat: pass raw data
  return panel.data;
}

export function EntityListWidget({ config: _config, data, panelWidth }: { config: Record<string, unknown>; data?: EntityListData; panelWidth?: number }) {
  if (!data) return null;

  const items = data.items ?? [];

  if (items.length === 0) {
    return (
      <div className="entity-list-widget entity-list-empty" data-testid="entity-list-widget">
        <p className="text-muted small text-center my-3">Нет данных</p>
      </div>
    );
  }

  const itemsClassName = panelWidth !== undefined && panelWidth >= 8
    ? 'entity-list-items entity-list-items--wide'
    : 'entity-list-items';

  return (
    <div className="entity-list-widget" data-testid="entity-list-widget">
      <div className={itemsClassName}>
        {items.map(item => (
          <EntityListItemView key={item.entity.nid} item={item} />
        ))}
      </div>
    </div>
  );
}

function EntityListItemView({ item }: { item: EntityListItemDto }) {
  const { entity, panels } = item;

  return (
    <div className="entity-list-item" data-testid="entity-list-item">
      <div className="entity-list-item-header d-flex align-items-center gap-2 px-3 py-2 border-bottom">
        <div
          className="entity-list-icon rounded-2 d-flex align-items-center justify-content-center"
          style={{ width: 32, height: 32, background: 'var(--bs-primary-bg-subtle)' }}
        >
          <i
            className={`las ${getBundleIcon(entity.bundle)}`}
            style={{ color: 'var(--bs-primary)', fontSize: '1rem' }}
          />
        </div>
        <a
          href={entity.url}
          className="fw-semibold text-truncate flex-grow-1 text-decoration-none small"
        >
          {entity.title}
        </a>
      </div>

      {panels.length > 0 && (
        <div className="entity-list-item-panels px-3 py-2">
          {panels.map((panel, idx) => (
            <div key={idx} className="entity-list-panel mb-2">
              <WidgetRenderer
                panel={{
                  id: `entity-${entity.nid}-panel-${idx}`,
                  type: panel.type as WidgetType,
                  config: panel.config,
                  x: 0, y: 0,
                  w: (panel.config as any)?._dw ?? 8,
                  h: (panel.config as any)?._dh ?? 2,
                  data: transformPanelData(panel),
                  _dataLoaded: true,
                } as unknown as Panel}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getBundleIcon(bundle: string): string {
  const icons: Record<string, string> = {
    process: 'la-project-diagram',
    robot: 'la-robot',
    task: 'la-tasks',
    system: 'la-server',
  };
  return icons[bundle] ?? 'la-file-alt';
}
